import type {
  AudioAdapter,
  AppLifecycleAdapter,
  HapticsAdapter,
  InstallStatusSnapshot,
  SharingAdapter,
  StorageAdapter,
} from './interfaces/index.js';

export type { InstallStatusSnapshot } from './interfaces/install-status.js';
import { registerAppServiceWorker } from './pwa/register-service-worker.js';
import {
  createWebAudioAdapter,
  createWebHapticsAdapter,
  createWebLifecycleAdapter,
  createWebSharingAdapter,
  createWebStorageAdapter,
} from './web/index.js';

interface BeforeInstallPromptEvent {
  preventDefault(): void;
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface NavigatorWithStandalone {
  standalone?: boolean;
}

export interface PlatformServices {
  readonly isOnline: boolean;
  readonly installStatus: InstallStatusSnapshot;
  readonly sharing: SharingAdapter;
  readonly haptics: HapticsAdapter;
  readonly audio: AudioAdapter;
  readonly lifecycle: AppLifecycleAdapter;
  readonly storage: StorageAdapter;
  subscribeOnline(listener: (online: boolean) => void): () => void;
  subscribeInstallStatus(listener: (status: InstallStatusSnapshot) => void): () => void;
  promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'>;
}

function readInstallStatus(): InstallStatusSnapshot {
  if (typeof window === 'undefined') {
    return { isInstalled: false, canInstall: false };
  }

  const isInstalled =
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as NavigatorWithStandalone).standalone === true;

  return {
    isInstalled,
    canInstall: false,
  };
}

export function createBrowserPlatformServices(): PlatformServices {
  let isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
  let installStatus = readInstallStatus();
  let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
  const onlineListeners = new Set<(online: boolean) => void>();
  const installListeners = new Set<(status: InstallStatusSnapshot) => void>();

  const sharing = createWebSharingAdapter();
  const haptics = createWebHapticsAdapter();
  const audio = createWebAudioAdapter();
  const lifecycle = createWebLifecycleAdapter();
  const storage = createWebStorageAdapter();

  function notifyOnline() {
    for (const listener of onlineListeners) listener(isOnline);
  }

  function notifyInstall() {
    for (const listener of installListeners) listener(installStatus);
  }

  function setInstallStatus(next: InstallStatusSnapshot) {
    installStatus = next;
    notifyInstall();
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => {
      isOnline = true;
      notifyOnline();
    });
    window.addEventListener('offline', () => {
      isOnline = false;
      notifyOnline();
    });

    window.addEventListener('beforeinstallprompt', (event) => {
      const installEvent = event as unknown as BeforeInstallPromptEvent;
      installEvent.preventDefault();
      deferredInstallPrompt = installEvent;
      setInstallStatus({
        ...installStatus,
        canInstall: true,
      });
    });

    window.addEventListener('appinstalled', () => {
      deferredInstallPrompt = null;
      setInstallStatus({
        isInstalled: true,
        canInstall: false,
      });
    });

    registerAppServiceWorker({
      beforeReload: () => lifecycle.flushPendingWork(),
    });
  }

  return {
    get isOnline() {
      return isOnline;
    },
    get installStatus() {
      return installStatus;
    },
    sharing,
    haptics,
    audio,
    lifecycle,
    storage,
    subscribeOnline(listener) {
      onlineListeners.add(listener);
      return () => onlineListeners.delete(listener);
    },
    subscribeInstallStatus(listener) {
      installListeners.add(listener);
      return () => installListeners.delete(listener);
    },
    async promptInstall() {
      if (!deferredInstallPrompt) {
        return 'unavailable';
      }

      await deferredInstallPrompt.prompt();
      const choice = await deferredInstallPrompt.userChoice;
      if (choice.outcome === 'accepted') {
        deferredInstallPrompt = null;
        setInstallStatus({
          isInstalled: true,
          canInstall: false,
        });
        return 'accepted';
      }

      return 'dismissed';
    },
  };
}

export function createTestPlatformServices(
  initial?: Partial<Pick<PlatformServices, 'isOnline' | 'installStatus'>>,
): PlatformServices & {
  _setOnline: (online: boolean) => void;
  _setInstallStatus: (status: InstallStatusSnapshot) => void;
} {
  let isOnline = initial?.isOnline ?? true;
  let installStatus = initial?.installStatus ?? {
    isInstalled: false,
    canInstall: false,
  };
  const onlineListeners = new Set<(online: boolean) => void>();
  const installListeners = new Set<(status: InstallStatusSnapshot) => void>();

  const sharing = createWebSharingAdapter();
  const haptics = createWebHapticsAdapter();
  const audio = createWebAudioAdapter();
  const lifecycle = createWebLifecycleAdapter();
  const storage = createWebStorageAdapter();

  return {
    get isOnline() {
      return isOnline;
    },
    get installStatus() {
      return installStatus;
    },
    sharing,
    haptics,
    audio,
    lifecycle,
    storage,
    subscribeOnline(listener) {
      onlineListeners.add(listener);
      return () => onlineListeners.delete(listener);
    },
    subscribeInstallStatus(listener) {
      installListeners.add(listener);
      return () => installListeners.delete(listener);
    },
    async promptInstall() {
      return 'unavailable';
    },
    _setOnline(online: boolean) {
      isOnline = online;
      for (const listener of onlineListeners) listener(isOnline);
    },
    _setInstallStatus(status: InstallStatusSnapshot) {
      installStatus = status;
      for (const listener of installListeners) listener(installStatus);
    },
  };
}
